import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Cpu, Layers } from "lucide-react";
const kanjiMap: Record<string, string> = {
  iphone: "TW-01",
  android: "TW-02",
  accessories: "TW-03",
  tablets: "TW-04",
  wearables: "TW-05",
  audio: "TW-06",
  refurbished: "TW-07",
  deals: "TW-08",
  premium: "TW-09",
  latest: "TW-10"
};
const metaMap: Record<string, string> = {
  iphone: "SYSTEM: IOS_ECOSYSTEM // CORE: A17_PRO",
  android: "SYSTEM: OPEN_SOURCE // CORE: SNAPDRAGON_G3",
  accessories: "SYSTEM: PERIPHERAL // TYPE: SHIELD_PRO",
  tablets: "SYSTEM: PAD_OS // TYPE: WORKSTATION",
  wearables: "SYSTEM: BIO_METRIC // TYPE: SENSOR_V2",
  audio: "SYSTEM: SONIC_ENGINE // TYPE: SPATIAL",
  refurbished: "SYSTEM: RE_CERTIFIED // STATUS: GRADE_A",
  deals: "SYSTEM: PROMO_UNIT // STATUS: ACTIVE",
  premium: "SYSTEM: LUX_TIER // TYPE: FOLDABLE",
  latest: "SYSTEM: NEW_RELEASE // STATUS: DEPLOYED"
};
function getAnnotation(slug: string, name: string): string {
  const key = slug?.toLowerCase() ?? "";
  if (kanjiMap[key]) return kanjiMap[key];
  const nameKey = name?.toLowerCase() ?? "";
  for (const k of Object.keys(kanjiMap)) {
    if (nameKey.includes(k)) return kanjiMap[k];
  }
  return "TW-CORE";
}
function getMeta(slug: string, name: string): string {
  const key = slug?.toLowerCase() ?? "";
  if (metaMap[key]) return metaMap[key];
  const nameKey = name?.toLowerCase() ?? "";
  for (const k of Object.keys(metaMap)) {
    if (nameKey.includes(k)) return metaMap[k];
  }
  return "SYSTEM: MOBILE_TECH // STATUS: OPTIMIZED";
}
function CollectionsGridConsumer() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border">
        <div className="lg:col-span-8 h-[600px] bg-card animate-pulse" />
        <div className="lg:col-span-4 grid grid-rows-2 gap-px">
          <div className="h-full bg-card animate-pulse" />
          <div className="h-full bg-card animate-pulse" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="border border-border p-16 text-center bg-card">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-6">
          [ ERROR_FETCHING_CATEGORIES ]
        </p>
        <Button onClick={refetch} variant="outline" className="font-mono uppercase tracking-widest">
          Re-Initialize
        </Button>
      </div>;
  }
  if (terms.length === 0) {
    return <div className="border border-border p-16 text-center bg-card">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">
          [ NO_DATA_FOUND ]
        </p>
      </div>;
  }
  const feature = terms[0];
  const grid = terms.slice(1, 4);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {}
      <Link to={feature.link || "#"} data-index={0} className="group lg:col-span-8 relative block bg-card border border-border overflow-hidden transition-all duration-500 hover:border-primary">
        <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        <div className="relative h-full flex flex-col lg:flex-row min-h-[500px]">
          <div className="flex-1 flex items-center justify-center p-12 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:20px_20px]" />
            {feature.image_url ? <img src={feature.image_url} data-wvc-srcset={`${feature.image_url}-wvc-srcset`} data-wvc-sizes={`${feature.image_url}-wvc-sizes`} alt={feature.name} loading="lazy" decoding="async" className="max-h-[400px] w-auto object-contain transition-transform duration-700 ease-out group-hover:scale-105" /> : <Cpu className="size-32 text-primary/20" />}
          </div>

          <div className="lg:w-2/5 p-10 lg:p-12 flex flex-col justify-end bg-background/50 backdrop-blur-sm border-l border-border">
            <div className="mb-8">
              <p className="text-[10px] font-mono uppercase tracking-[0.4em] text-primary mb-4">
                [ PRIMARY_MODULE ]
              </p>
              <h3 className="font-default text-2xl lg:text-3xl font-extrabold text-foreground leading-none mb-4">
                {feature.name}
                <span className="block text-primary text-sm font-mono mt-2 tracking-widest">
                  [{getAnnotation(feature.slug, feature.name)}]
                </span>
              </h3>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed uppercase tracking-wider">
                {getMeta(feature.slug, feature.name)}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-widest group-hover:text-primary transition-colors">
              Explore Hardware <ArrowRight className="size-4" />
            </div>
          </div>
        </div>
      </Link>

      {}
      <div className="lg:col-span-4 flex flex-col gap-8">
        {grid.map((term, i) => <Link key={term.id} to={term.link || "#"} data-index={i + 1} className="group relative flex-1 block bg-card border border-border overflow-hidden transition-all duration-500 hover:border-primary">
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative h-full flex flex-col p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-primary mb-2">
                    [ MOD_{i + 2} ]
                  </p>
                  <h3 className="font-default text-2xl font-bold text-foreground">
                    {term.name}
                  </h3>
                  <span className="text-[10px] font-mono text-primary tracking-widest">
                    [{getAnnotation(term.slug, term.name)}]
                  </span>
                </div>
                {term.image_url ? <img src={term.image_url} data-wvc-srcset={`${term.image_url}-wvc-srcset`} data-wvc-sizes={`${term.image_url}-wvc-sizes`} alt={term.name} loading="lazy" decoding="async" className="size-16 object-contain transition-transform duration-500 group-hover:scale-110" /> : <Layers className="size-8 text-primary/20" />}
              </div>
              <div className="mt-auto">
                <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-4">
                  {getMeta(term.slug, term.name)}
                </p>
                <div className="h-px w-full bg-border group-hover:bg-primary/30 transition-colors" />
              </div>
            </div>
          </Link>)}
      </div>
    </div>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 4,
  orderby: "count",
  order: "desc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function DeviceEcosystemCategories() {
  return <section data-section-id="4076" id="device-ecosystem-categories" className="bg-background text-foreground py-24 lg:py-32 px-6 lg:px-12 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:40px_40px]" />
      
      <div className="max-w-[1440px] mx-auto relative z-10">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 lg:mb-28">
          <div className="lg:col-span-8">
            <p className="text-xs font-mono uppercase tracking-[0.4em] text-primary mb-8 flex items-center gap-3">
              <span className="w-8 h-px bg-primary" />
              02 // HARDWARE_ECOSYSTEM
            </p>
            <h2 className="font-default text-5xl md:text-7xl xl:text-8xl font-extrabold text-foreground leading-[0.85] tracking-tighter">
              MOBILE HARDWARE <br />
              <span className="text-primary">ENGINEERED</span> FOR <br />
              PERFORMANCE.
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col justify-end">
            <div className="border-l-2 border-primary pl-6 py-2">
              <p className="text-sm lg:text-base font-default text-muted-foreground leading-relaxed max-w-sm">
                Every device in our catalog is selected for peak efficiency. Explore our
                ecosystem by category &mdash; from flagship smartphones to essential
                mobile accessories.
              </p>
            </div>
          </div>
        </div>

        {}
        <EcommerceTermsProvider className="device-ecosystem-grid" wp_term_query={wp_term_query}>
          <CollectionsGridConsumer />
        </EcommerceTermsProvider>

        {}
        <div className="mt-20 lg:mt-28 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 pt-12 border-t border-border">
          <div className="flex items-center gap-4">
            <div className="size-2 bg-primary" />
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-muted-foreground">
              TECHWIRE_MOBILE // CERTIFIED_HARDWARE_V2.4
            </p>
          </div>
          <Link to="/ShopPage" className="group relative inline-flex items-center gap-6 px-8 py-4 bg-primary text-white font-mono text-xs uppercase tracking-[0.2em] transition-all duration-300 hover:bg-foreground">
            View Full Catalog
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-2" />
          </Link>
        </div>
      </div>
    </section>;
}